import React, { useState, useRef } from "react";
import "../styles/pages/MakingCustom.scss";
import SelectOption from "../components/selectbook/selectOption/SelectOption";
import InsertPicture from "../components/selectbook/insertPicture/InsertPicture";
import SelectType from "../components/selectbook/selectStatic/SelectType";
import SelectGender from "../components/selectbook/selectStatic/SelectGender";
import InputName from "../components/selectbook/selectStatic/InputName";
import SelectDrawType from "../components/selectbook/selectStatic/SelectDrawType";
import OXQuizLoading from "../components/utils/loading/QXQuizLoading";
import Result from "../components/selectbook/result/Result";
import SelectStory from "../components/selectbook/selectStatic/SelectStory";
import {
  customCreateCharacter,
  getCharacterStatus,
  createCustomStory,
  createCustomStoryImage,
} from "../api/customAPI";
import { getStoryImageStatus } from "../api/StoryAPI";
import CustomStartModal from "../components/utils/modal/CustomStartModal";
import { useNavigate } from "react-router-dom";
import NotSelectModal from "../components/utils/modal/NotSelectModal";
import { MAKING_CUSTOM_STEPS } from "../utils/Constants";
import SlidePuzzleLoading from "../components/utils/loading/SlidePuzzleLoading";
import MagicLoading from "../components/utils/loading/MagicLoading";
import NikiPencilLoading from "../components/utils/loading/NikiPencilLoading";
import useSound from "../hooks/common/useSound";
import CustomCompleteModal from "../components/utils/modal/CustomCompleteModal";
import useBGM from "../hooks/common/useBGM";
import MusicToggleButton from "../components/utils/button/MusicToggleButton";

export default function MakingCustom() {
  const navigate = useNavigate();
  const [step, setStep] = useState(MAKING_CUSTOM_STEPS[0]);
  const [showLoading, setShowLoading] = useState(false);
  const [showChangeLoading, setShowChangeLoading] = useState(false);
  const [showSaveLoading, setShowSaveLoading] = useState(false);
  const [showStart, setShowStart] = useState(true);
  const [type, setType] = useState("");
  const [storyId, setStoryId] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [characterJobId, setCharacterJobId] = useState("");
  const [storyPrompt, setStoryPrompt] = useState([]);
  const [sceneImages, setSceneImages] = useState([]);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [drawType, setDrawType] = useState("");
  const [userImage, setUserImage] = useState(null);
  const [showNotSelectModal, setShowNotSelectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [storyList, setStoryList] = useState([]);
  const [modalMessage, setModalMessage] = useState("");
  const [isChargeError, setIsChargeError] = useState(false);
  const USER_ID = localStorage.getItem("userID");
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const playOptionSelectSound = useSound("/audio/customBook/option-select.mp3");
  const playNextBtnSound = useSound("/audio/customBook/next-btn.mp3");
  const playPrevBtnSound = useSound("/audio/customBook/prev-btn.mp3");

  // 시작 모달 종료 후 결과 화면 전까지 배경음악 재생 (로딩 중에는 정지)
  const isSelectMusicActive =
    !showStart && step !== "result" && !showLoading && !showChangeLoading;
  useBGM(isSelectMusicActive ? "/audio/customBook/select-music.mp3" : null);

  // 로딩 화면(SlidePuzzleLoading, MagicLoading) 배경음악 재생
  const isLoadingMusicActive = showLoading || showChangeLoading;
  useBGM(isLoadingMusicActive ? "/audio/customBook/loading-music.mp3" : null);

  const handleNext = () => {
    const currentIndex = MAKING_CUSTOM_STEPS.indexOf(step);
    if (currentIndex === MAKING_CUSTOM_STEPS.length - 1) {
      setShowCompleteModal(true);
    } else {
      setStep(MAKING_CUSTOM_STEPS[currentIndex + 1]);
    }
  };

  const handleStartCreating = () => {
    setShowCompleteModal(false);
    handleMaking();
  };

  const handlePrev = () => {
    const currentIndex = MAKING_CUSTOM_STEPS.indexOf(step);
    if (currentIndex > 0) {
      setStep(MAKING_CUSTOM_STEPS[currentIndex - 1]);
    }
  };

  const handleMaking = async () => {
    setShowLoading(true);

    try {
      // 1. 캐릭터 생성 시작
      const res = await customCreateCharacter({
        user_id: USER_ID,
        story_id: storyId,
        format: type,
        prompt: selectedOptions,
        photo: userImage,
        name: name,
        gender: gender,
        style: drawType,
      });

      if (!res?.success) {
        if (res?.errCode === "Magicbean_ERR_00") {
          const error = new Error(
            "마법콩이 부족합니다. 마법콩을 충전해주세요.",
          );
          error.isCharge = true;
          throw error;
        }
        throw new Error("캐릭터 생성 정보 등록에 실패했습니다.");
      }

      const { job_id } = res.data;
      setCharacterJobId(job_id);

      // 2. 캐릭터 상태 확인 (완료될 때까지 대기)
      const characterRes = await characterStatus(job_id);
      if (!characterRes?.success) {
        throw new Error("캐릭터 생성 중 오류가 발생했습니다.");
      }

      // 3. 소설 내용(스토리) 생성
      const storyRes = await createCustomStory({
        user_id: USER_ID,
        character_job_id: job_id,
      });

      if (!storyRes?.success) {
        if (storyRes?.errCode === "Magicbean_ERR_00") {
          const error = new Error(
            "마법콩이 부족합니다. 마법콩을 충전해주세요.",
          );
          error.isCharge = true;
          throw error;
        }
        throw new Error("소설 내용을 구성하는 중 오류가 발생했습니다.");
      }
      setStoryPrompt(storyRes.data.items);

      // 4. 소설 이미지 생성 시작
      const storyImageRes = await createCustomStoryImage({
        user_id: USER_ID,
        character_job_id: job_id,
        items: storyRes.data.items,
      });

      if (!storyImageRes?.success) {
        if (storyImageRes?.errCode === "Magicbean_ERR_00") {
          const error = new Error(
            "마법콩이 부족합니다. 마법콩을 충전해주세요.",
          );
          error.isCharge = true;
          throw error;
        }
        throw new Error("소설 이미지를 생성하는 중 오류가 발생했습니다.");
      }

      // 5. 이미지 생성 상태 확인 (완료될 때까지 대기)
      const imageStatus = await getImageStatus(storyImageRes.data.batch_id);
      if (!imageStatus?.success) {
        throw new Error("이미지를 가져오는 중 오류가 발생했습니다.");
      }

      if (imageStatus.data.all_completed) {
        setSceneImages(imageStatus.data.images);
        setStep("result");
      } else {
        throw new Error("이미지 생성이 완료되지 않았습니다.");
      }
    } catch (error) {
      console.error("[handleMaking Error]", error);
      handleAlert(
        error.message || "도서를 생성하는 중 알 수 없는 오류가 발생했습니다.",
        error.isCharge,
      );
    } finally {
      setShowLoading(false);
    }
  };

  const characterStatus = async (job_id) => {
    const res = await getCharacterStatus({ job_id });

    if (res.success && res.data?.status === "done") {
      return res;
    } else if (res.success && res.data?.status === "pending") {
      await sleep(2000);
      return await characterStatus(job_id);
    } else {
      await sleep(2000);
      return await characterStatus(job_id);
    }
  };

  const getImageStatus = async (batch_id) => {
    const res = await getStoryImageStatus({ batch_id, user_id: USER_ID });

    if (res.success && res.data?.status === "completed") {
      return res;
    } else if (res.success && res.data?.status === "processing") {
      await sleep(2000);
      return await getImageStatus(batch_id);
    } else {
      await sleep(2000);
      return await getImageStatus(batch_id);
    }
  };

  const handleNavigateHome = () => {
    navigate("/selecting");
  };

  const handleAlert = (message, isCharge = false) => {
    setModalMessage(message);
    setIsChargeError(isCharge);
    setShowNotSelectModal(true);
  };

  const handleCharge = () => {
    window.open("https://ai-magic4u.co.kr/payment/pay?route=story", "_blank");
    setShowNotSelectModal(false);
  };

  return (
    <>
      {step === "result" ? (
        <Result
          type={type}
          sceneImages={sceneImages}
          storyPrompt={storyPrompt}
          setIsChangeLoading={setShowChangeLoading}
          user_id={USER_ID}
          character_job_id={characterJobId}
          getImageStatus={getImageStatus}
          storyId={storyId}
        />
      ) : (
        <div className="wrap">
          <figure className="home" onClick={handleNavigateHome}>
            <img
              src="/images/utils/modal/customStartModal/home.svg"
              alt="home"
            />
          </figure>
          {step === "story" && (
            <SelectStory
              onNext={handleNext}
              setStoryId={(id) => {
                setStoryId(id);
              }}
              onAlert={handleAlert}
              onSelectSound={playOptionSelectSound}
              onNextSound={playNextBtnSound}
              setStoryList={setStoryList} // 스토리 목록을 받아서 제목을 찾기 위함
            />
          )}
          {step === "picture" && (
            <InsertPicture
              onNext={(img) => {
                setUserImage(img);
                handleNext();
              }}
              onPrev={handlePrev}
              USER_ID={USER_ID}
              onAlert={handleAlert}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
          {step === "gender" && (
            <SelectGender
              onNext={handleNext}
              onPrev={handlePrev}
              setGender={setGender}
              gender={gender}
              onAlert={handleAlert}
              onSelectSound={playOptionSelectSound}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
          {step === "name" && (
            <InputName
              onNext={handleNext}
              onPrev={handlePrev}
              name={name}
              setName={setName}
              onAlert={handleAlert}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
          {step === "drawType" && (
            <SelectDrawType
              onNext={handleNext}
              onPrev={handlePrev}
              setDrawType={setDrawType}
              drawType={drawType}
              onAlert={handleAlert}
              onSelectSound={playOptionSelectSound}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
          {step === "option" && (
            <SelectOption
              onNext={handleNext}
              onPrev={handlePrev}
              storyId={storyId}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              onAlert={handleAlert}
              onSelectSound={playOptionSelectSound}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
          {step === "genre" && (
            <SelectType
              onNext={handleNext}
              onPrev={handlePrev}
              setType={setType}
              type={type}
              onAlert={handleAlert}
              onSelectSound={playOptionSelectSound}
              onNextSound={playNextBtnSound}
              onPrevSound={playPrevBtnSound}
            />
          )}
        </div>
      )}
      {step !== "result" && (
        <div className="music-toggle-btn-wrapper">
          <MusicToggleButton />
        </div>
      )}
      {showLoading && <SlidePuzzleLoading />}
      {showChangeLoading && <MagicLoading />}
      {showStart && (
        <CustomStartModal
          onNavigateHome={handleNavigateHome}
          onStart={() => setShowStart(false)}
          onSkip={() => setShowStart(false)}
        />
      )}
      {showNotSelectModal && (
        <NotSelectModal
          message={modalMessage}
          onClose={() => setShowNotSelectModal(false)}
          isCharge={isChargeError}
          onCharge={handleCharge}
        />
      )}
      {showCompleteModal && (
        <CustomCompleteModal
          onStart={handleStartCreating}
          onClose={() => setShowCompleteModal(false)}
          data={{
            storyTitle:
              storyList.find((s) => s.story_id === storyId)?.plan_title ||
              "선택 안됨",
            gender,
            name,
            drawType,
            selectedOptions,
            type,
            userImage,
          }}
        />
      )}
    </>
  );
}
